import React, { useContext } from 'react';
import { Popover } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
// import AsperLogo from 'assets/svg/AsperLogo';
import Hamburger from 'assets/svg/Hamburger';
import BellOutline from 'assets/svg/BellOutline';
import TimeStampNavbar from 'components/molecules/TimeStampNavbar';
import CustomAsperButton from 'components/atoms/CustomAsperButton';
import { extractEmailAndName, getAuthUser, getAuthUserMail, getConfig } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { ASK_ASPER_GLOBAL_ICON_CLICK, TOP_NAV_ASKASPER_CLICK, USER_MANUAL_CLICKED } from 'utils/mixpanelEventNames';
import { useCurrentCustomerLogo } from 'hooks/customerLogo/useCurrentCustomerLogo';

interface Props {
    logOut: any;
    onToggleRail: () => void;
}

// AskAsper access is limited to explicitly allowed emails (allowedAskAsperUsers from FE config)
// plus any internal user whose email ends with @asper.ai.
// If allowedAskAsperUsers is empty, AskAsper is shown to all users.
const isAllowedAskAsperUser = (allowedAskAsperUsers: string[]): boolean => {
    const currentUserEmail = getAuthUserMail().trim().toLowerCase();
    if (allowedAskAsperUsers.length === 0 || currentUserEmail.endsWith('@asper.ai')) return true;
    return allowedAskAsperUsers.some((email) => (email || '').trim().toLowerCase() === currentUserEmail);
};

/**
 * Navbar V2 header. Navigation moved out of here into NavRail + NavPanel, so this keeps only
 * the identity and utility controls. The Settings entry point moved to the rail's gear icon.
 */
function NavbarV2({ logOut, onToggleRail }: Props) {
    const location = useLocation();
    const navigate = useNavigate();
    const { appConfig } = useContext(UserContext);

    const commonConfig = getConfig('commonConfig', appConfig);
    const showLogo = commonConfig?.showLogoInNavbar || false;
    const showAskAsper = Boolean(
        commonConfig?.enableAskAsper && isAllowedAskAsperUser(commonConfig?.allowedAskAsperUsers || []),
    );
    const { logoSrc: navbarLogo, onLogoLoadError } = useCurrentCustomerLogo();

    const notificationsContent = () => (
        <div className="w-[25rem]">
            <div className="flex items-center mb-3">
                <div className="flex items-center gap-2">
                    <BellOutline />
                    <span className="text-sm font-semibold text-gray-900">Notifications</span>
                </div>
            </div>
            <div className="flex items-center justify-center py-6 text-sm text-gray-400">You're all caught up</div>
        </div>
    );

    const profileContent = () => (
        <div className="bg-white min-w-[12rem] rounded-b-lg font-medium font-xs">
            <div className="text-purpleFont mb-2 px-1">{extractEmailAndName()}</div>
            <div
                className="w-full py-1 mt-4 bg-purpleFont rounded flex justify-center items-center cursor-pointer"
                onClick={() => logOut()}
            >
                <span className="text-white">Logout</span>
            </div>
        </div>
    );

    return (
        <nav
            id="main-navbar"
            className="navbar-v2-height z-[60] w-full border-b border-filterV2Border font-md flex items-center justify-between px-4 fixed bg-white"
        >
            <div className="flex items-center gap-4">
                <button
                    type="button"
                    aria-label="Toggle navigation"
                    onClick={onToggleRail}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-filterV2TextPrimary hover:bg-filterV2PrimaryBg"
                >
                    <Hamburger />
                </button>
                {/* <AsperLogo /> */}
                {showLogo && (
                    <img
                        style={{
                            maxWidth: commonConfig?.logoWidth,
                            height: commonConfig?.logoHeight,
                            objectFit: 'contain',
                        }}
                        src={navbarLogo}
                        alt={commonConfig?.clientName || ''}
                        onError={onLogoLoadError}
                    />
                )}
                {process.env.REACT_APP_FRESHDESK_MANUAL_URL !== 'null' && (
                    <a
                        onClick={() => trackEventMixpanel(USER_MANUAL_CLICKED)}
                        target="_blank"
                        href={process.env.REACT_APP_FRESHDESK_MANUAL_URL}
                        className="font-md text-purpleFont hover:text-purpleFont"
                    >
                        {'User Manual'}
                    </a>
                )}
            </div>
            <div className="flex justify-center items-center gap-3">
                {/* Mount point for AskAsper eval-platform controls (portaled in from AskAsper) */}
                <div id="navbar-extra-controls" className="flex items-center gap-2" />
                {showAskAsper && (
                    <CustomAsperButton
                        onClick={() => {
                            trackEventMixpanel(TOP_NAV_ASKASPER_CLICK, { page_name: location.pathname });
                            trackEventMixpanel(ASK_ASPER_GLOBAL_ICON_CLICK, { page_name: location.pathname });
                            navigate('/ask-asper');
                        }}
                    />
                )}
                {/* Notifications: empty state only until there is a feed behind it */}
                <Popover
                    trigger="click"
                    showArrow={false}
                    placement="bottomRight"
                    content={notificationsContent}
                    overlayInnerStyle={{
                        borderRadius: '4px',
                    }}
                >
                    <button
                        type="button"
                        aria-label="Notifications"
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-filterV2TextPrimary hover:bg-filterV2PrimaryBg"
                    >
                        <BellOutline />
                    </button>
                </Popover>
                <span className="flex justify-center items-center mr-3 gap-x-7">
                    <TimeStampNavbar />
                </span>
                <span className="relative">
                    <Popover
                        overlayInnerStyle={{ position: 'relative', top: -5 }}
                        trigger={'hover'}
                        showArrow={false}
                        placement="bottomRight"
                        content={profileContent}
                    >
                        <span className="flex justify-center items-center rounded-full cursor-pointer bg-bgBackground avatar-size">
                            <span className="text-center text-purpleFont font-xxs font-bold">{getAuthUser()}</span>
                        </span>
                    </Popover>
                </span>
            </div>
        </nav>
    );
}

export default NavbarV2;
